import './SendOrderDialog.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Spinner } from '@Components/Spinner';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { emailSelector, fullNameSelector } from '@src/redux/user';
import { OrderMailComposer } from './OrderMailComposer';
import { orderPlatforms, SendViaType } from './send-order-utils';
import { Order } from './streaming-buying-types';
import { makeOrderPdf, useStreamingOrder } from './use-streaming-buying';

interface SendOrderDialogProps {
  orderId: number;
  network: string;
  via: SendViaType;
  onHide: () => void;
  onSent: () => void;
}

// Emails an order's IO to the network from outside Streaming Buying (Plan & Buy, Agent Order
// Approvals), with its send view: the network's contacts and an editable email beside the IO.
export const SendOrderDialog = ({
  orderId,
  network,
  via,
  onHide,
  onSent
}: SendOrderDialogProps) => (
  <Modal
    show
    onHide={onHide}
    size='xl'
    centered
    dialogClassName='send-order-dialog'
    data-testid='send-order-dialog'
  >
    <Modal.Header closeButton>
      <Modal.Title>
        Send order #{orderId} to {network}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className='send-order-body'>
      <SendOrderBody
        orderId={orderId}
        network={network}
        via={via}
        onHide={onHide}
        onSent={onSent}
      />
    </Modal.Body>
  </Modal>
);

const SendOrderBody = ({ orderId, network, via, onHide, onSent }: SendOrderDialogProps) => {
  const setError = useSetError();
  const { data, error } = useStreamingOrder(String(orderId));

  useEffect(() => {
    if (error) {
      setError({ message: `Couldn't load order ${orderId}. ${error.message}`, reportError: error });
      onHide();
    }
  }, [error, orderId, setError, onHide]);

  if (!data) return <Spinner />;
  return (
    <OrderSend
      order={data}
      orderId={orderId}
      network={network}
      via={via}
      onHide={onHide}
      onSent={onSent}
    />
  );
};

interface OrderSendProps extends SendOrderDialogProps {
  order: Order;
}

const OrderSend = ({ order, orderId, network, via, onHide, onSent }: OrderSendProps) => {
  const fullName = useAppSelector(fullNameSelector);
  const email = useAppSelector(emailSelector);
  const [sending, setSending] = useState(false);

  const platforms = useMemo(() => orderPlatforms(order.flights), [order]);
  const generatePDF = useCallback(
    () => makeOrderPdf({ id: orderId, fullName, email, via }),
    [orderId, fullName, email, via]
  );

  if (platforms.length === 0) {
    return (
      <p data-testid='send-order-no-property'>
        None of order {orderId}&apos;s flights has a property, so there&apos;s no network contact to
        send it to.
      </p>
    );
  }
  return (
    <OrderMailComposer
      order={order}
      network={network}
      platforms={platforms}
      generatePDF={generatePDF}
      sending={sending}
      setSending={setSending}
      onSent={onSent}
      onClose={onHide}
    />
  );
};
